import { memo, useState } from '../../lib/teact/teact';

import buildClassName from '../../util/buildClassName';
import { CRM_BACKEND_URL as BACKEND_URL } from '../../util/crmBackendUrl';

import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';

import styles from './TelethonAuthModal.module.scss';

type OwnProps = {
  isOpen: boolean;
  configuredPhone?: string;
  onClose: NoneToVoidFunction;
  onSuccess: NoneToVoidFunction;
};

const TelethonAuthModal = ({
  isOpen,
  configuredPhone = '+59157746549',
  onClose,
  onSuccess,
}: OwnProps) => {
  const [phone, setPhone] = useState(configuredPhone);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [phoneCodeHash, setPhoneCodeHash] = useState('');
  const [requires2fa, setRequires2fa] = useState(false);
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleRequestCode = useLastCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const res = await fetch(`${BACKEND_URL}/telethon/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        if (data.phone_code_hash) {
          setPhoneCodeHash(data.phone_code_hash);
        }
        setStep('verify');
        setSuccessMessage(data.message || `Código enviado exitosamente a ${phone}`);
      } else {
        const rawDetail = data.detail || data.message || 'Error al solicitar código a Telegram';
        let cleanMsg = typeof rawDetail === 'string' ? rawDetail : JSON.stringify(rawDetail);
        try {
          const parsed = JSON.parse(cleanMsg);
          if (parsed && typeof parsed.detail === 'string') {
            cleanMsg = parsed.detail;
          }
        } catch {
          // Mantener cleanMsg
        }
        setErrorMessage(cleanMsg);
      }
    } catch (e: any) {
      setErrorMessage(`Error de conexión: ${e.message || 'No se pudo comunicar con el servidor'}`);
    } finally {
      setIsLoading(false);
    }
  });

  const handleVerifyCode = useLastCallback(async () => {
    if (!code.trim()) {
      setErrorMessage('Por favor ingrese el código de 5 dígitos.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const payload: { phone: string; code: string; password?: string; phone_code_hash?: string } = {
        phone,
        code: code.trim(),
        phone_code_hash: phoneCodeHash || undefined,
      };
      if (requires2fa && password) {
        payload.password = password.trim();
      }

      const res = await fetch(`${BACKEND_URL}/telethon/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setSuccessMessage('¡Autenticación exitosa! El daemon 24/7 ha quedado activado permanentemente.');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1800);
      } else if (data.requires_2fa) {
        setRequires2fa(true);
        if (data.phone_code_hash) {
          setPhoneCodeHash(data.phone_code_hash);
        }
        setErrorMessage('Esta cuenta tiene Verificación en Dos Pasos (2FA) activada. Ingrese su contraseña adicional.');
      } else {
        const rawDetail = data.detail || data.message || 'Código incorrecto o expirado.';
        let cleanMsg = typeof rawDetail === 'string' ? rawDetail : JSON.stringify(rawDetail);
        try {
          const parsed = JSON.parse(cleanMsg);
          if (parsed && typeof parsed.detail === 'string') {
            cleanMsg = parsed.detail;
          }
        } catch {
          // Mantener cleanMsg
        }
        setErrorMessage(cleanMsg);
      }
    } catch (e: any) {
      setErrorMessage(`Error de conexión al verificar: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  });

  if (!isOpen) return undefined;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.iconWrapper}>
              <Icon name="channel" />
            </div>
            <div>
              <h3 className={styles.headerTitle}>Vincular Cuenta MTProto (Telethon 24/7)</h3>
              <p className={styles.headerSubtitle}>
                Posgrado Medicina UMSS • Respuesta Autónoma Desatendida
              </p>
            </div>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          <div className={styles.infoBanner}>
            <Icon name="info" />
            <div>
              Esta vinculación genera una sesión criptográfica permanente en el servidor.
              Permite que la IA responda consultas en noches, fines de semana y feriados de Cochabamba.
            </div>
          </div>

          {errorMessage && (
            <div className={styles.alertError}>
              ⚠️
              {' '}
              {errorMessage}
            </div>
          )}

          {successMessage && (
            <div className={styles.alertSuccess}>
              ✅
              {' '}
              {successMessage}
            </div>
          )}

          {step === 'request' ? (
            <div className={styles.formGroup}>
              <label className={styles.label}>Teléfono Institucional (+591...):</label>
              <input
                type="text"
                className={styles.input}
                value={phone}
                onChange={(e) => setPhone((e.target).value)}
                placeholder="+59157746549"
              />
              <p className={styles.headerSubtitle}>
                Se solicitará un código oficial de inicio de sesión a Telegram para este número.
              </p>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => setStep('verify')}
              >
                ¿Ya recibiste un código en tu app de Telegram o SMS? Ingresar código directamente →
              </button>
            </div>
          ) : (
            <>
              <div className={styles.formGroup}>
                <label className={styles.label}>Código de Verificación (5 dígitos):</label>
                <input
                  type="text"
                  className={buildClassName(styles.input, styles.codeInput)}
                  value={code}
                  maxLength={6}
                  onChange={(e) => setCode((e.target).value)}
                  placeholder="12345"
                  autoFocus
                />
                <p className={styles.headerSubtitle}>
                  Revisa los chats oficiales de Telegram en tu teléfono o la bandeja de SMS.
                </p>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => setStep('request')}
                >
                  ← Volver a solicitar código
                </button>
              </div>

              {requires2fa && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>Contraseña 2FA (Dos Pasos):</label>
                  <input
                    type="password"
                    className={styles.input}
                    value={password}
                    onChange={(e) => setPassword((e.target).value)}
                    placeholder="Contraseña de la cuenta"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button type="button" className={styles.btnCancel} onClick={onClose} disabled={isLoading}>
            Cancelar
          </button>
          {step === 'request' ? (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleRequestCode}
              disabled={isLoading || !phone.trim()}
            >
              <Icon name="arrow-right" />
              <span>{isLoading ? 'Solicitando...' : 'Enviar Código a Telegram'}</span>
            </button>
          ) : (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleVerifyCode}
              disabled={isLoading || !code.trim()}
            >
              <Icon name="check" />
              <span>{isLoading ? 'Verificando...' : 'Verificar y Activar 24/7'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(TelethonAuthModal);
